<template>
  <div>
    <van-field readonly clickable right-icon="arrow-down" placeholder="请选择" :value="valueYMD" @click="popup = true" />
    <van-popup ref="popup" v-model="popup" round position="bottom" :close-on-click-overlay="false">
      <date-picker
        :value="currentDate"
        :min-date="minDate"
        :max-date="maxDate"
        type="datetime"
        @cancel="popup = false"
        @confirm="onConfirm"
      />
    </van-popup>
  </div>
</template>

<script>
import DateTimeHour from './DateTimeHour'
import DatePicker from 'vant/es/datetime-picker/DatePicker'
import { getTrueValue, getMonthEndDay } from 'vant/es/datetime-picker/utils'
import { isDate } from 'vant/es/utils/validate/date'
import pad from 'lodash/padStart'

function padZero(val) {
  return pad(val.toString(), 2, '0')
}

export default {
  name: 'DateTimeSecond',
  components: {
    DatePicker: {
      extends: DatePicker,
      computed: {
        ranges() {
          const { maxYear, maxDate, maxMonth, maxHour, maxMinute, maxSecond } = this.getBoundary(
            'max',
            this.innerValue ? this.innerValue : this.minDate
          )

          const { minYear, minDate, minMonth, minHour, minMinute, minSecond } = this.getBoundary(
            'min',
            this.innerValue ? this.innerValue : this.minDate
          )

          let result = [
            {
              type: 'year',
              range: [minYear, maxYear]
            },
            {
              type: 'month',
              range: [minMonth, maxMonth]
            },
            {
              type: 'day',
              range: [minDate, maxDate]
            },
            {
              type: 'hour',
              range: [minHour, maxHour]
            },
            {
              type: 'minute',
              range: [minMinute, maxMinute]
            },
            {
              type: 'second',
              range: [minSecond, maxSecond]
            }
          ]

          switch (this.type) {
            case 'date':
              result = result.slice(0, 3)
              break
            case 'year-month':
              result = result.slice(0, 2)
              break
            case 'month-day':
              result = result.slice(1, 3)
              break
            case 'datehour':
              result = result.slice(0, 4)
              break
          }

          if (this.columnsOrder) {
            const columnsOrder = this.columnsOrder.concat(result.map(column => column.type))
            result.sort((a, b) => columnsOrder.indexOf(a.type) - columnsOrder.indexOf(b.type))
          }

          return result
        }
      },
      methods: {
        formatValue(value) {
          if (!isDate(value)) {
            return null
          }

          let minDate = new Date(this.minDate)
          let maxDate = new Date(this.maxDate)
          const dateMethods = {
            year: 'getFullYear',
            month: 'getMonth',
            day: 'getDate',
            hour: 'getHours',
            minute: 'getMinutes',
            second: 'getSeconds'
          }
          if (this.originColumns) {
            const dateColumns = this.originColumns.map(({ type, values }, index) => {
              const { range } = this.ranges[index]
              const minDateVal = minDate[dateMethods[type]]()
              const maxDateVal = maxDate[dateMethods[type]]()
              const min = type === 'month' ? +values[0] - 1 : +values[0]
              const max = type === 'month' ? +values[values.length - 1] - 1 : +values[values.length - 1]

              return {
                type,
                values: [
                  minDateVal < range[0] ? Math.max(minDateVal, min) : min || minDateVal,
                  maxDateVal > range[1] ? Math.min(maxDateVal, max) : max || maxDateVal
                ]
              }
            })

            if (this.type === 'month-day') {
              const year = (this.innerValue || this.minDate).getFullYear()
              dateColumns.unshift({ type: 'year', values: [year, year] })
            }

            const dates = Object.keys(dateMethods)
              .map(type => dateColumns.filter(item => item.type === type)[0]?.values)
              .filter(item => item)
            minDate = new Date(...dates.map(val => getTrueValue(val[0])))
            maxDate = new Date(...dates.map(val => getTrueValue(val[1])))
          }

          value = Math.max(value, minDate.getTime())
          value = Math.min(value, maxDate.getTime())

          return new Date(value)
        },
        getBoundary(type, value) {
          const boundary = this[`${type}Date`]
          const year = boundary.getFullYear()
          let month = 1
          let date = 1
          let hour = 0
          let minute = 0
          let second = 0
          if (type === 'max') {
            month = 12
            date = getMonthEndDay(value.getFullYear(), value.getMonth() + 1)
            hour = 23
            minute = 59
            second = 59
          }

          if (value.getFullYear() === year) {
            month = boundary.getMonth() + 1
            if (value.getMonth() + 1 === month) {
              date = boundary.getDate()
              if (value.getDate() === date) {
                hour = boundary.getHours()
                if (value.getHours() === hour) {
                  minute = boundary.getMinutes()
                  if (value.getMinutes() === minute) {
                    second = boundary.getSeconds()
                  }
                }
              }
            }
          }

          return {
            [`${type}Year`]: year,
            [`${type}Month`]: month,
            [`${type}Date`]: date,
            [`${type}Hour`]: hour,
            [`${type}Minute`]: minute,
            [`${type}Second`]: second
          }
        },
        updateInnerValue() {
          const { type } = this
          const indexes = this.getPicker().getIndexes()
          const getValue = type => {
            let index = 0
            this.originColumns.forEach((column, columnIndex) => {
              if (type === column.type) {
                index = columnIndex
              }
            })
            const { values } = this.originColumns[index]
            return getTrueValue(values[indexes[index]])
          }

          let year
          let month
          let day
          if (type === 'month-day') {
            year = (this.innerValue || this.minDate).getFullYear()
            month = getValue('month')
            day = getValue('day')
          } else {
            year = getValue('year')
            month = getValue('month')
            day = type === 'year-month' ? 1 : getValue('day')
          }

          const maxDay = getMonthEndDay(year, month)
          day = day > maxDay ? maxDay : day

          let hour = 0
          let minute = 0
          let second = 0

          if (type === 'datehour') {
            hour = getValue('hour')
          }

          if (type === 'datetime') {
            hour = getValue('hour')
            minute = getValue('minute')
            second = getValue('second')
          }

          const value = new Date(year, month - 1, day, hour, minute, second)

          this.innerValue = this.formatValue(value)
        },
        updateColumnValue() {
          const value = this.innerValue ? this.innerValue : this.minDate
          const { formatter } = this

          const values = this.originColumns.map(column => {
            switch (column.type) {
              case 'year':
                return formatter('year', `${value.getFullYear()}`)
              case 'month':
                return formatter('month', padZero(value.getMonth() + 1))
              case 'day':
                return formatter('day', padZero(value.getDate()))
              case 'hour':
                return formatter('hour', padZero(value.getHours()))
              case 'minute':
                return formatter('minute', padZero(value.getMinutes()))
              case 'second':
                return formatter('second', padZero(value.getSeconds()))
              default:
                // no default
                return null
            }
          })

          this.$nextTick(() => {
            this.getPicker().setValues(values)
          })
        }
      }
    }
  },
  extends: DateTimeHour,
  props: {},
  data() {
    return {
      popupMS: false,
      valueHMS: '',
      valueYMD: '',
      columns: []
    }
  },
  created() {
    if (this.value.length) {
      this.valueYMD = this.value
    } else {
      let uw = this.$watch('value', val => {
        this.valueYMD = val
        uw()
      })
    }
  },
  watch: {},
  methods: {
    onChangeValue(picker, values) {
      // console.log(values)
      // const minuteColumns = this.getMinutes(values);
      // console.log(minuteColumns,)
    },
    onConfirm(value) {
      let year = value.getFullYear()
      let m = padZero(value.getMonth() + 1)
      let d = padZero(value.getDate())
      let h = padZero(value.getHours())
      let mm = padZero(value.getMinutes())
      let ss = padZero(value.getSeconds())
      this.popup = false
      this.valueYMD = `${year}-${m}-${d} ${h}:${mm}:${ss}`
      this.$emit('input', this.valueYMD)
    }
  }
}
</script>
